import * as React from "react";
import { useState, useEffect } from "react";
import MarginMenu from "./MarginMenu";
import Auth from "./Auth";
import { makeStyles, Spinner } from "@fluentui/react-components";
import { onAuthStateChanged, ensureUserProfile } from "../../services/auth";
import { User } from 'firebase/auth';

const useStyles = makeStyles({
  root: {
    minHeight: "100vh",
  },
  loadingContainer: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  footer: {
    position: "fixed",
    bottom: "8px",
    left: "0",
    right: "0",
    textAlign: "center",
    fontSize: "10px",
    color: "#888",
    pointerEvents: "none",
    zIndex: 1000
  }
});

const AuthenticatedGuard: React.FC<{ user: User | null; loading: boolean; officeReady: boolean; redirectError: string | null; children: React.ReactNode }> = ({ user, loading, officeReady, redirectError, children }) => {
  const guardStyles = useStyles();

  if (loading) {
    return (
      <div className={guardStyles.loadingContainer}>
        <Spinner label="Loading Margin..." />
      </div>
    );
  }

  if (!user) {
    return <Auth initialError={redirectError} />;
  }

  if (!officeReady) {
    return (
      <div className={guardStyles.loadingContainer}>
        <Spinner label="Waking up Word..." />
      </div>
    );
  }

  return <>{children}</>;
};

const App: React.FC = () => {
  const appStyles = useStyles();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [hasSyncToken, setHasSyncToken] = useState(!!localStorage.getItem('marginpro_graph_token'));
  const [graphToken, setGraphToken] = useState<string | null>(localStorage.getItem('marginpro_graph_token'));
  const [tenantId, setTenantId] = useState<string | undefined>(undefined);
  const [userRole, setUserRole] = useState<string>("Contributor");
  const [officeReady, setOfficeReady] = useState(false);
  const [redirectError, setRedirectError] = useState<string | null>(null);

  useEffect(() => {
    console.log("MarginPro: App component mounted. Initializing auth...");

    let officeTimeout: any = null;

    // Ensure Office is ready before doing anything sensitive
    if (typeof Office !== "undefined") {
      console.log("MarginPro: Office SDK detected. Initializing context handshake...");
      Office.onReady((info) => {
        console.log("MarginPro: Office context confirmed ready. Host:", info.host, "Platform:", info.platform);
        if (officeTimeout) {
          clearTimeout(officeTimeout);
        }
        setOfficeReady(true);
      });

      // Failsafe timeout: if Office.onReady doesn't fire within 3.5 seconds, force proceed.
      officeTimeout = setTimeout(() => {
        console.warn("MarginPro: Office.onReady timed out after 3.5s. Forcing officeReady to true.");
        setOfficeReady(true);
      }, 3500);
    } else {
      console.log("MarginPro: Office SDK not found. Assuming standalone browser environment.");
      setOfficeReady(true); // Fallback for local dev/browser
    }

    let unsubscribe = () => {};
    try {
      unsubscribe = onAuthStateChanged((currentUser) => {
        console.log("MarginPro: Auth State Changed. User:", currentUser?.uid || "null");
        setUser(currentUser);
        
        if (currentUser) {
          console.log("MarginPro: Valid user detected, ensuring profile in background...");
          
          // Restore token from localStorage synchronously if it exists, so we don't wait for Firestore
          const localToken = localStorage.getItem('marginpro_graph_token');
          if (localToken) {
            setGraphToken(localToken);
            setHasSyncToken(true);
          }

          // Fetch profile asynchronously in the background
          ensureUserProfile(currentUser)
            .then((profile) => {
              console.log("MarginPro: Profile sync complete in background.");
              if (profile.role) {
                setUserRole(profile.role);
              }

              // Restore Graph token from localStorage if present.
              // Graph tokens are NOT stored in Firestore (security policy) —
              // a fresh token is obtained via SSO on every new session.
              const localToken = localStorage.getItem('marginpro_graph_token');
              if (localToken) {
                setGraphToken(localToken);
                setHasSyncToken(true);
              }

              if (profile.tenantId) {
                setTenantId(profile.tenantId);
              }
            })
            .catch((err) => {
              console.error("Profile sync failed:", err);
            });
        }
        
        // Set loading to false immediately so the UI is unblocked
        setLoading(false);
      });
    } catch (authError) {
      console.error("MarginPro: Synchronous error in auth listener setup:", authError);
      setLoading(false); // Make sure we don't hang if firebase auth setup throws
    }

    return () => {
      console.log("MarginPro: App component unmounting.");
      if (officeTimeout) {
        clearTimeout(officeTimeout);
      }
      if (typeof unsubscribe === "function") {
        unsubscribe();
      }
    };
  }, []);

  return (
    <AuthenticatedGuard user={user} loading={loading} officeReady={officeReady} redirectError={redirectError}>
      <MarginMenu 
        user={user!} 
        hasSyncToken={hasSyncToken} 
        setHasSyncToken={setHasSyncToken} 
        graphToken={graphToken}
        setGraphToken={setGraphToken}
        tenantId={tenantId}
        role={userRole}
      />
      {process.env.VITE_VERSION && (
        <div className={appStyles.footer}>
          {process.env.VITE_VERSION} ({process.env.VITE_ENVIRONMENT})
        </div>
      )}
    </AuthenticatedGuard>
  );
};

export default App;
